import React from 'react';
import { format } from 'date-fns';
import {
  Button,
  Divider,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  Typography
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  ArrowForward as ArrowRightIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import { Mastercode, Order } from 'types/schema-generated-types';
import getOrderNumber from 'utilities/get-order-number';
import { MastercodeActionClickHandler } from '../OrdersPage';
import { ORDER_DATE_FORMAT } from '../utils/constants';
import MastercodeCard from './MastercodeCard';

export type Props = {
  order: Order;
  onActivateMastercodeClick: MastercodeActionClickHandler;
  onTransferMastercodeClick: MastercodeActionClickHandler;
};

const OrderCard = ({ order, onActivateMastercodeClick, onTransferMastercodeClick }: Props) => {
  const classes = useStyles();

  const activeMastercodes = order.mastercodes?.filter(m => !m.dateExpired) ?? [];

  return (
    <ExpansionPanel>
      <ExpansionPanelSummary
        aria-controls="order-content"
        id="order-header"
        classes={{ content: classes.panelSummaryContent }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Grid container spacing={0} alignItems="center" justify="space-between">
          <Grid item>
            <Typography variant="h3">Order No. {getOrderNumber(order)}</Typography>
            <Typography variant="subtitle1">
              Order Date: {format(new Date(order.dateOrdered), ORDER_DATE_FORMAT)}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={<ArrowRightIcon />}
              className={classes.headerButton}
              onClick={onTransferMastercodeClick({ order })}
            >
              Transfer All
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<CheckIcon />}
              className={classes.headerButton}
              onClick={onActivateMastercodeClick({ order })}
            >
              Activate All
            </Button>
          </Grid>
        </Grid>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.detailsContainer}>
        <Grid container spacing={0} direction="column">
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Grid container spacing={3} className={classes.productCardsContainer}>
              {activeMastercodes.map(
                (mastercode: Mastercode): React.ReactElement => (
                  <Grid item key={mastercode.code} xs={12}>
                    <MastercodeCard
                      mastercode={mastercode}
                      onActivateMastercodeClick={onActivateMastercodeClick({ order, mastercode })}
                      onTransferMastercodeClick={onTransferMastercodeClick({ order, mastercode })}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

export default OrderCard;

const useStyles = makeStyles((theme: Theme) => ({
  panelSummaryContent: {
    margin: `${theme.spacing(2.5)}px 0`
  },
  headerButton: {
    marginLeft: theme.spacing(),
    marginRight: theme.spacing()
  },
  detailsContainer: {
    paddingTop: 0
  },
  productCardsContainer: {
    marginTop: theme.spacing(2)
  }
}));
